import {AiToolImpl} from "@me8eon/aiclient";
import {mustBeObjectWithFields, mustBeString} from "@me8eon/validate";
import {ElasticSearchConfig, ElasticSearchHit, ElasticSearchKnnSearchResponse} from "@me8eon/ai.tools.utils";
import { EnvironmentConfig } from "@me8eon/config";
import { JwtTokenFn } from "@me8eon/authentication";

/**
 * Converts an Elasticsearch hit to a formatted string.
 * @param hit
 * @param index
 */
const hitToString = (hit: ElasticSearchHit, index: number): string => {
    return `**Document ${index + 1}**:
     ${hit._source.title && `- Title: ${hit._source.title}\n`}
     - Score: ${hit._score}, 
     - Index: ${hit._index}, 
     - ID: ${hit._id}
     - Source: """${JSON.stringify(hit._source)}"""`;
};

type ToolArgs = {
    query: string
}

type ToolImpl = AiToolImpl<ToolArgs>

type Tool = (config: ElasticSearchConfig, jwtTokenFn: JwtTokenFn) => ToolImpl

export const DeepSearchToolImpl: Tool = (esConfig, jwtTokenFn) => {
    return {
        type: "deepSearch",
        toolDefn: {
            type: 'function',
            name: "deepSearch",
            description: "This tool performs a deep search on specified Elasticsearch index using semantic_text fields- one field with vector (e5-small) and another with sparse (elser). It retrieves the most relevant documents based on the provided query.",
            parameters: {
                type: "object",
                required: ["query"],
                additionalProperties: false,
                properties: {
                    query: {
                        type: "string",
                        description: "The search query string used to find relevant documents. Should be a concise and clear representation of the information being sought. This query HAS TO BE IN ENGLISH as the underlying models are trained primarily on English data.",
                    }
                },
            },
        },
        validateArgs: mustBeObjectWithFields({query: mustBeString}),
        execute: async (indices, input) => {
            try {
                const {
                    axios,
                    blacklistedFields = [],
                } = esConfig;
                const {query} = input;
                if (indices.length === 0) throw new Error("No indicies provided to knn tool");
                const top = 5;
                const indiciesAsString = indices.join(",");
                const url = `${EnvironmentConfig.elasticSearchProxyUrl}/${indiciesAsString}/_search`;
                const headers = {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${await jwtTokenFn()}`,
                };

                const body = {
                    "_source": {
                        "excludes": [...blacklistedFields,
                            "denied_countries",
                            "denied_companies",
                            "denied_users",
                            "allowed_countries",
                            "allowed_companies",
                            "allowed_users",
                            "allowed_all_countries",
                            "allowed_all_companies",
                            "allowed_all_users",
                            "dense_vector",
                            "sparse_vector"],
                    },
                    "size": top,
                    "retriever": {
                        "text_similarity_reranker": {
                            "retriever": {
                                "rrf": {
                                    "rank_window_size": 100,
                                    "rank_constant": 60,
                                    "retrievers": [
                                        {
                                            "standard": {
                                                "query": {
                                                    "match": {
                                                        "sparse_vector": query,
                                                    }
                                                }
                                            }
                                        },
                                        {
                                            "standard": {
                                                "query": {
                                                    "match": {
                                                        "dense_vector": query,
                                                    }
                                                }
                                            }
                                        }
                                    ]
                                }
                            },
                            "field": "semantic_text",
                            "inference_id": "cross-encoder-rerank",
                            "inference_text": query,
                            "rank_window_size": 5
                        },
                    },
                    "highlight": {
                        "fields": {
                            "sparse_vector": {
                                "type": "semantic",
                                "number_of_fragments": 3,
                                "order": "score"
                            },
                            "dense_vector": {
                                "type": "semantic",
                                "number_of_fragments": 3,
                                "order": "score"
                            }
                        }
                    }
                };


                const response = await axios.post<ElasticSearchKnnSearchResponse>(url, body, {headers: headers});
                const topDocuments = response.data?.hits?.hits?.slice(0, top).map((hit, index) => hitToString(hit, index));
                const content = `Here are the top ${topDocuments.length} documents found:\n\n${topDocuments.join("\n\n")}`;
                return {
                    value: content,
                }

            } catch (e: any) {
                return {
                    value: "An error occurred while performing the deep search.",
                    _meta: {
                        errorMessage: String(e?.message ?? e),
                    }
                };
            }

        },
    };
};

